// UnitPlanModal.jsx

import React from "react";
import {
    Dialog,
    DialogContent,
    IconButton,
    Typography,
    Box,
    Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import imgType from '../icons/unitType.png';
import imgArea from '../icons/area.png';
import imgBath from '../icons/bath.png';
import imgBed from '../icons/bedroom.png';
import { X } from "../icons/x";

const InfoRow = ({ icon, label, value }) => (
    <Box
        sx={{
            display: "flex",
            alignItems: "center",
            mb: 2.5,
            color: "var(--gold)",
            gap: 1
        }}
    >
        <Box sx={{ mr: { xs: 1, md: 2 }, display: "flex", alignItems: "center", ml: 2 }}>
            <img className="w-[24px] sm:w-[32px]" src={icon} />
        </Box>

        <Typography
            sx={{
                fontFamily: "Caslon",
                fontSize: {
                    xs: "1rem",
                    md: "1.1rem",
                    lg: "1.35rem",
                },
            }}
        >
            {label}
        </Typography>

        <Box
            sx={{
                flex: 1,
                borderBottom: "1px dashed var(--gold)",
                mx: 2,
                opacity: 0.7,
            }}
        />

        <Typography
            sx={{
                color: "var(--white)",
                fontSize: {
                    xs: "1.1rem",
                    md: "1.25rem",
                    lg: "1.5rem",
                },
                fontFamily: "Caslon",
                mr: 2
            }}
        >
            {value}
        </Typography>
    </Box>
);

export default function Unit({
    open,
    onClose,
    currUnit,
    imageUrl,
    pdfUrl
}) {
    const { i18n, t } = useTranslation();
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            disableAutoFocus
            sx={{
                "& .MuiPaper-root": {
                    borderRadius: 0,
                    boxShadow: "none",
                    overflow: "hidden",
                    height: "calc(100vh - 60px)",
                    maxHeight: { xs: "auto", md: "550px" }
                },
                "& .MuiBackdrop-root": {
                    backgroundColor: "#FAF2F0bb",
                    backdropFilter: "blur(4px)",
                }
            }}
        >
            <DialogContent
                sx={{
                    p: 0,
                    overflow: "visible",
                    background: "transparent",
                    height: "100%"
                }}
            >
                <Box
                    sx={{
                        width: "calc(100vw - 4rem)",
                        maxWidth: 1160,
                        backgroundColor: "var(--blue)",
                        display: "flex",
                        // Stack vertically on mobile (image on top), row on desktop
                        flexDirection: {
                            xs: "column",
                            sm: "row",
                        },
                        position: "relative",
                        overflowY: "auto",
                        height: "100%"
                    }}
                >
                    {/* RIGHT PANEL — rendered first in DOM so it appears on top on mobile via order */}
                    <Box
                        className={pdfUrl && "pdfParent"}
                        onClick={() => pdfUrl && window.open(pdfUrl + "?" + Date.now(), "_blank")}
                        sx={{
                            // On mobile: fixed aspect-ratio height so image is visible and doesn't crop
                            height: {
                                xs: "45vw",
                                sm: "auto",
                            },
                            // On desktop: take remaining space
                            flex: {
                                xs: "none",
                                sm: 1,
                            },
                            order: {
                                xs: 0, // image first on mobile
                                sm: 1, // image second (right) on desktop
                            },
                            backgroundColor: "var(--white2)",
                            borderTopLeftRadius: {
                                xs: 60,
                                md: 120,
                            },
                            position: "relative",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: {
                                xs: 2,
                                sm: 6,
                            },
                            m: {
                                xs: 2,
                                sm: 4,
                            },
                            ml: {
                                xs: 2,
                                sm: 2,
                            },
                            mb: {
                                xs: 0,
                                sm: 4,
                            }

                        }}
                    >
                        {!imageUrl ? (
                            <Typography
                                sx={{
                                    color: "var(--blue)",
                                    opacity: 0.29,
                                    fontSize: {
                                        xs: "2rem",
                                        md: "3rem",
                                        lg: "4rem",
                                    },
                                    fontStyle: "italic",
                                    textAlign: "center",
                                    fontFamily: "Caslon",
                                    width: {
                                        xs: "80%",
                                        md: "250px",
                                        lg: "350px",
                                    },
                                }}
                            >
                                {t("pdfNote")}
                            </Typography>
                        ) :
                            <Box
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    position: "relative"
                                }}
                            >
                                <Box
                                    className="pdfImg"
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        backgroundImage: imageUrl
                                            ? `url(${imageUrl + "?" + Date.now()})`
                                            : "none",
                                        backgroundSize: "contain",
                                        backgroundRepeat: "no-repeat",
                                        backgroundPosition: "center",
                                    }}
                                >
                                </Box>
                                <Typography
                                    className="pdfText"
                                    sx={{
                                        color: "var(--blue)",
                                        fontSize: {
                                            xs: "0.75rem",
                                            sm: "1.1rem"
                                        },
                                        fontStyle: "italic",
                                        textAlign: "center",
                                        fontFamily: "Caslon",
                                        width: "42%",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        position: "absolute",
                                        border: "solid var(--blue) 1px",
                                        padding: "10px 15px",
                                        pointerEvents: "none",
                                        opacity: 0
                                    }}
                                >
                                    {t("viewPlan")}
                                </Typography>
                            </Box>
                        }

                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            zIndex: 50,
                            position: "absolute",
                            top: {
                                xs: 22,
                                sm: 42,
                            },
                            right: {
                                xs: 22,
                                sm: 42,
                            },
                            backgroundColor: "var(--blue)",
                            color: "var(--gold)",
                            borderRadius: 0,
                            width: 42,
                            height: 42,
                            "&:hover": {
                                backgroundColor: "var(--gold)",
                                color: "var(--white)"
                            },
                        }}
                    >
                        <X width={15} height={15} color="inherit" />
                    </IconButton>

                    {/* LEFT PANEL */}
                    <Box
                        sx={{
                            width: {
                                xs: "100%",
                                sm: "43%",
                            },
                            order: {
                                xs: 1, // info below image on mobile
                                sm: 0, // info first (left) on desktop
                            },
                            p: 4,
                            pr: { xs: 4, md: 2 },
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            // On desktop: fill panel height; on mobile: size to content
                            height: {
                                xs: "auto",
                                md: "100%",
                            },
                            overflowY: "auto"
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    color: "var(--white)",
                                    fontSize: {
                                        xs: "1.75rem",
                                        md: "2.5rem",
                                        lg: "3.5rem",
                                    },
                                    fontFamily: "Caslon",
                                    lineHeight: 1,
                                    fontWeight: 300,
                                }}
                            >
                                {t("unit") + " " + currUnit.unit}
                            </Typography>

                            <Typography
                                sx={{
                                    color: "var(--gold)",
                                    fontSize: {
                                        xs: "1rem",
                                        md: "1.2rem",
                                        lg: "1.6rem",
                                    },
                                    fontFamily: "Caslon",
                                    mt: 1,
                                }}
                            >
                                {currUnit.name}
                            </Typography>

                            <Divider
                                sx={{
                                    borderColor: "var(--gold)",
                                    opacity: 0.5,
                                    my: {
                                        xs: 2,
                                        md: 3,
                                        lg: 4,
                                    },
                                }}
                            />

                            <Box
                                sx={{
                                    mt: {
                                        xs: 3,
                                        md: 5,
                                        lg: 6,
                                    },
                                }}
                            >
                                <InfoRow
                                    icon={imgType}
                                    label={t("unitType")}
                                    value={currUnit.type?.replace(".5", "½")}
                                />

                                <InfoRow
                                    icon={imgArea}
                                    label={t("area")}
                                    value={currUnit.size + " " + t("ft")}
                                />

                                <InfoRow
                                    icon={imgBed}
                                    label={t("nbBedrooms")}
                                    value={currUnit.bedrooms}
                                />

                                <InfoRow
                                    icon={imgBath}
                                    label={t("nbBathrooms")}
                                    value={currUnit.bathrooms}
                                />
                            </Box>
                        </Box>
                        <Box>
                            <Divider
                                sx={{
                                    borderColor: "var(--gold)",
                                    opacity: 0.5,
                                    mt: 2,
                                }}
                            />

                            <Typography
                                sx={{
                                    color: "var(--gold)",
                                    opacity: 0.85,
                                    fontSize: {
                                        xs: "0.6rem",
                                        md: "0.75rem",
                                        lg: "0.85rem",
                                    },
                                    fontStyle: "italic",
                                    whiteSpace: "pre-line",
                                    fontFamily: "Caslon",
                                    mt: 2,
                                }}
                            >
                                {t("modalFooter")}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog >
    );
}